import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import type { IVlmBlock } from './vlm-layer-panel.types';

export interface VlmFeatureDialogData {
  vlm: IVlmBlock;
  authUrlFn: (url: string) => string;
}

/**
 * Dialog that shows the VLM analysis data for a single feature clicked on the map.
 */
@Component({
  selector: 'lib-vlm-feature-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose, MatIcon],
  template: `
    <h2 mat-dialog-title class="d-flex align-items-center gap-2">
      <mat-icon>analytics</mat-icon>
      <span>
        {{ data.vlm.vlm_label ?? 'VLM Analysis' }}
        @if (data.vlm.vlm_score !== undefined && data.vlm.vlm_score !== null) {
          <span class="vlm-dialog__score-badge {{ scoreClass }}">{{ data.vlm.vlm_score }}</span>
        }
      </span>
    </h2>

    <mat-dialog-content class="vlm-dialog__content">
      @if (imageUrl; as url) {
        <div class="vlm-dialog__image-wrapper">
          <img [src]="url" alt="VLM preview" class="vlm-dialog__image" />
        </div>
      }

      @if (data.vlm.vlm_narrative) {
        <p class="vlm-dialog__narrative">{{ data.vlm.vlm_narrative }}</p>
      }

      @if (data.vlm.vlm_rationale) {
        <p class="vlm-dialog__rationale">{{ data.vlm.vlm_rationale }}</p>
      }

      @if (propertyEntries.length > 0) {
        <table class="vlm-card__props-table w-100 mt-2">
          @for (prop of propertyEntries; track prop.key) {
            <tr>
              <td class="vlm-card__prop-key">{{ prop.key }}</td>
              <td class="vlm-card__prop-value">{{ prop.value }}</td>
            </tr>
          }
        </table>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button matButton mat-dialog-close>Close</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .vlm-dialog__content { min-width: 320px; max-width: 480px; }
    .vlm-dialog__image-wrapper { width: 100%; aspect-ratio: 1; overflow: hidden; border-radius: var(--mat-sys-shape-medium, 8px); margin-bottom: 12px; }
    .vlm-dialog__image { width: 100%; height: 100%; object-fit: cover; }
    .vlm-dialog__narrative { font-size: 0.875rem; line-height: 1.5; color: var(--mat-sys-on-surface); margin-bottom: 8px; }
    .vlm-dialog__rationale { font-size: 0.8125rem; color: var(--mat-sys-on-surface-variant); font-style: italic; margin-bottom: 8px; }
    .vlm-dialog__score-badge { padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600; margin-left: 8px; }
    .score-high  { background: var(--mat-sys-tertiary);        color: var(--mat-sys-on-tertiary); }
    .score-medium{ background: var(--mat-sys-secondary);       color: var(--mat-sys-on-secondary); }
    .score-low   { background: var(--mat-sys-error);           color: var(--mat-sys-on-error); }
    .score-none  { background: var(--mat-sys-surface-variant); color: var(--mat-sys-on-surface-variant); }
  `],
})
export class VlmFeatureDialogComponent {
  readonly data = inject<VlmFeatureDialogData>(MAT_DIALOG_DATA);

  get imageUrl(): string | null {
    const raw = this.data.vlm.image_url ?? this.data.vlm.preview_url ?? null;
    return raw ? this.data.authUrlFn(raw) : null;
  }

  get scoreClass(): string {
    const score = this.data.vlm.vlm_score;
    if (score === undefined || score === null) return 'score-none';
    if (score >= 70) return 'score-high';
    if (score >= 40) return 'score-medium';
    return 'score-low';
  }

  get propertyEntries(): { key: string; value: string }[] {
    if (!this.data.vlm.vlm_properties) return [];
    return Object.entries(this.data.vlm.vlm_properties).map(([key, value]) => ({
      key,
      value: value === null || value === undefined ? '—' : String(value),
    }));
  }
}
